import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { X, Share2, CheckCircle, Mail } from 'lucide-react'
import { shareResource } from '../../services/api'

interface ShareModalProps {
  isOpen: boolean
  onClose: () => void
  resourceId: string
  resourceType: string
  title: string
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export default function ShareModal({
  isOpen,
  onClose,
  resourceId,
  resourceType,
  title,
}: ShareModalProps) {
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')
  const [success, setSuccess] = useState(false)

  const shareMutation = useMutation({
    mutationFn: async (recipientEmail: string) => {
      await shareResource(resourceId, recipientEmail)
    },
    onSuccess: () => {
      setSuccess(true)
      setTimeout(() => {
        onClose()
        setSuccess(false)
        setEmail('')
      }, 2000)
    },
  })

  const handleShare = () => {
    if (!email.trim()) {
      setEmailError('Email is required')
      return
    }
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }
    setEmailError('')
    shareMutation.mutate(email.trim())
  }

  const handleEmailChange = (value: string) => {
    setEmail(value)
    if (emailError) setEmailError('')
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        {success ? (
          <div className="text-center py-6">
            <CheckCircle className="mx-auto h-10 w-10 text-green-500 mb-3" />
            <p className="text-base font-medium text-gray-900">Resource shared</p>
            <p className="text-sm text-gray-500 mt-1">
              Sent to {email}
            </p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Share2 className="h-5 w-5 text-acad-600" />
                <h2 className="text-lg font-semibold text-gray-900">Share Resource</h2>
              </div>
              <button
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Resource info */}
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 mb-4">
              <p className="text-sm font-medium text-gray-900 truncate">{title}</p>
              <span className="inline-flex items-center rounded-full bg-acad-50 px-2 py-0.5 text-xs font-medium text-acad-600 capitalize mt-1">
                {resourceType}
              </span>
            </div>

            {/* Email input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Recipient Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleShare()}
                  placeholder="colleague@university.edu"
                  className={`w-full rounded-lg border pl-10 pr-3 py-2 text-sm outline-none transition-colors ${
                    emailError
                      ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-500'
                      : 'border-gray-300 focus:border-acad-500 focus:ring-1 focus:ring-acad-500'
                  }`}
                  autoFocus
                />
              </div>
              {emailError && <p className="mt-1 text-xs text-red-500">{emailError}</p>}
            </div>

            {/* Error */}
            {shareMutation.isError && (
              <div className="mt-3 rounded-lg bg-red-50 border border-red-200 p-2 text-xs text-red-700">
                Failed to share resource. Please check the email and try again.
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={onClose}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleShare}
                disabled={shareMutation.isPending || !email.trim()}
                className="rounded-lg bg-acad-500 px-4 py-2 text-sm font-medium text-white hover:bg-acad-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {shareMutation.isPending ? 'Sharing...' : 'Share'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
